import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, graphConfig } from '../config/authConfig';

interface UserInfo {
  name: string;
  email: string;
  jobTitle: string;
  photoUrl: string | null;
}

interface AuthContextType {
  userInfo: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = accounts.length > 0;

  const getAccessToken = async (): Promise<string | null> => {
    if (accounts.length === 0) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.error('Error acquiring token silently:', error);
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        return response.accessToken;
      } catch (popupError) {
        console.error('Error acquiring token with popup:', popupError);
        return null;
      }
    }
  };

  const fetchUserInfo = async () => {
    if (accounts.length === 0) {
      setUserInfo(null);
      setIsLoading(false);
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      const headers = new Headers();
      headers.append('Authorization', `Bearer ${token}`);

      const profileResponse = await fetch(`${graphConfig.graphMeEndpoint}?$select=displayName,mail,jobTitle`, {
        headers,
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const profileData = await profileResponse.json();

      let photoUrl: string | null = null;
      try {
        const photoResponse = await fetch(graphConfig.graphMePhotoEndpoint, {
          headers,
        });

        if (photoResponse.ok) {
          const blob = await photoResponse.blob();
          photoUrl = URL.createObjectURL(blob);
        }
      } catch (photoError) {
        console.log('User photo not available:', photoError);
      }

      setUserInfo({
        name: profileData.displayName || accounts[0].name || 'Usuario',
        email: profileData.mail || accounts[0].username || '',
        jobTitle: profileData.jobTitle || '',
        photoUrl,
      });
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUserInfo({
        name: accounts[0].name || 'Usuario',
        email: accounts[0].username || '',
        jobTitle: '',
        photoUrl: null,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const login = () => {
    instance.loginRedirect(loginRequest);
  };

  const logout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  };

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      fetchUserInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, inProgress]);

  const value: AuthContextType = {
    userInfo,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
